import { PhotoItem } from "../../item"

export type SelectionData = string | Set<string>
export type SelectionAction = "select" | "select_list" | "deselect" | "deselect_list" | "update" | "set"
export type SelectionSortingOptions = "datetaken_newold" | "datetaken_oldnew" | "filename_az" | "filename_za"
export type SelectionListener = (action: SelectionAction) => void

export class SelectionManager {
  // do we care about the order???
  private readonly list: Map<string, PhotoItem> = new Map()
  private readonly listeners: Set<SelectionListener> = new Set()
  /**
   * Last item for which the selection is set, a potential start item
   * for the 'rangeSelect' operation
   */
  private lastSelectedItem: PhotoItem | null = null

  private sortingOption: SelectionSortingOptions | undefined

  constructor(sortingOption?: SelectionSortingOptions) {
    this.sortingOption = sortingOption
  }

  setSortingOption(sortingOption: SelectionSortingOptions) {
    this.sortingOption = sortingOption
  }

  getSize() {
    return this.list.size
  }

  getLastSelectedItem(): PhotoItem | null {
    return this.lastSelectedItem
  }

  select(item: PhotoItem): void {
    const initSize = this.getSize()
    this.list.set(item.id, item)
    if (initSize < this.getSize()) {
      this.lastSelectedItem = item
      this.emit("select")
    }
  }

  deselect(item: PhotoItem): void {
    const initSize = this.getSize()
    this.list.delete(item.id)
    if (initSize > this.getSize()) {
      this.lastSelectedItem = null
      this.emit("deselect")
    }
  }

  isSelected(item: PhotoItem): boolean {
    return this.list.has(item.id)
  }

  /**
   * returning array list of items. ordered if sortingOption is provided
   */
  getSelectedItems(): PhotoItem[] {
    const arrayList: PhotoItem[] = Array.from(this.list.values())
    return this.sortingOption ? this.sortItems(arrayList) : arrayList
  }

  /**
   * this is needed in case if we update properties of item
   * or assign new item with same id, we need to let all listeners know about it
   */
  update(item: PhotoItem) {
    if (this.list.has(item.id)) {
      this.list.set(item.id, item)
      this.emit("update")
    }
  }

  /**
   * Replace the current selection set
   */
  setItems(items: Iterable<PhotoItem>) {
    const remainder = new Map(this.list)
    // add new list to selected
    for (const item of items) {
      this.list.set(item.id, item)
      // remove rest of the items in the list
      remainder.delete(item.id)
    }
    // also remove remaining ids from previous list
    remainder.forEach((item) => this.list.delete(item.id))
    this.lastSelectedItem = null
    this.emit("set")
  }

  bulkSelect(items: Iterable<PhotoItem>) {
    const initSize = this.getSize()
    for (const item of items) {
      this.list.set(item.id, item)
    }
    if (initSize < this.getSize()) {
      // Send the selected item IDs
      this.emit("select_list")
    }
  }

  bulkDeselect(items: Iterable<PhotoItem>) {
    const initSize = this.getSize()
    for (const item of items) {
      this.list.delete(item.id)
    }
    if (initSize > this.getSize()) {
      // Send the deselected item IDs
      this.emit("deselect_list")
    }
  }

  /**
   * select range of items, starting from last selected
   * if the last selected is not set, then it will select only untilItem
   * on lastSelectedItem available, it will select range from
   * lastSelectedItem to untilItem, regardless the direction
   */
  rangeSelect(untilItem: PhotoItem, allItems: PhotoItem[]): void {
    // if we don't have last selected, then wwe don't have any range selection
    if (this.lastSelectedItem) {
      let start = allItems.indexOf(this.lastSelectedItem)
      let end = allItems.indexOf(untilItem)
      // if the list is range selected from bottom to top, swap it
      if (start > end) {
        ;[start, end] = [end, start]
      }
      for (let i = start; i <= end; i++) {
        this.list.set(allItems[i].id, allItems[i])
      }
      // notify list selection
      this.emit("select_list")
    } else {
      // if there is no last selected, then do regular select
      this.select(untilItem)
    }
  }

  addListener(listener: SelectionListener): void {
    this.listeners.add(listener)
  }

  removeListener(listener: SelectionListener): void {
    this.listeners.delete(listener)
  }

  private emit(action: SelectionAction): void {
    this.listeners.forEach((listener) => listener(action))
  }

  sortItems(items: PhotoItem[]): PhotoItem[] {
    switch (this.sortingOption) {
      case "datetaken_newold":
        return items.sort((item1, item2) => this.compareByDateTaken(item1, item2))
      case "datetaken_oldnew":
        return items.sort((item1, item2) => this.compareByDateTaken(item1, item2, true))
      case "filename_az":
        return items.sort((item1, item2) => this.compareByName(item1, item2))
      case "filename_za":
        return items.sort((item1, item2) => this.compareByName(item2, item1))
      default:
        return items
    }
  }

  compareByDateTaken(item1: PhotoItem, item2: PhotoItem, inverted = false): number {
    if (item1.dateTaken > item2.dateTaken) {
      return inverted ? 1 : -1
    }
    if (item1.dateTaken < item2.dateTaken) {
      return inverted ? -1 : 1
    }
    return 0
  }

  compareByName(item1: PhotoItem, item2: PhotoItem): number {
    return item1.name.localeCompare(item2.name)
  }
}
